// Waste container list (Stage 6 plan §D "pages/WasteContainers.vue"): filter chips, rows and
// group totals as i18n keys and parameters. Pure: no Vue, no API calls, so node:test can load it.
// Quantities stay in their own unit; litres are never shown as kg (L6).

export const WASTE_VIEWS = [
  { view: "all", key: "waste.list.filter_all", color: "primary" },
  { view: "not_weighed", key: "waste.list.filter_not_weighed", color: "amber" },
];
export const DEFAULT_VIEW = "all";
export const QUERY_MAX = 40;

// The API filters by name in lower case with single spaces.
export function cleanQuery(value) {
  if (typeof value !== "string") return "";
  return value.trim().replace(/\s+/g, " ").slice(0, QUERY_MAX);
}

export function filterChips(counts = {}) {
  return WASTE_VIEWS.map((chip) => ({
    ...chip,
    count: Number((counts || {})[chip.view]) || 0,
  }));
}

export function formatNumber(value, locale = "it") {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(
    Number(value) || 0,
  );
}

export function formatDate(value, locale = "it") {
  if (!value) return "";
  // A civil date (YYYY-MM-DD) is shown as it is; an instant in Rome time.
  const civil = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = civil ? new Date(`${value}T12:00:00Z`) : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: civil ? "UTC" : "Europe/Rome",
  }).format(date);
}

// What the current filling holds. An area counts car bodies (plus any kg); the rest a quantity.
export function recordedText(row, locale = "it") {
  const recorded = (row && row.recorded) || {};
  if (row && row.type === "area") {
    return {
      key: "waste.content.bodies",
      params: { count: recorded.bodies || 0 },
    };
  }
  if (!recorded.value) return { key: "waste.content.empty", params: {} };
  return {
    key: "waste.content.recorded",
    params: {
      value: formatNumber(recorded.value, locale),
      unit: recorded.unit,
      vehicles: recorded.vehicles || 0,
    },
  };
}

// The current weighing, or the amber "Non pesato" chip.
export function weighingView(latestWeighing, locale = "it") {
  if (!latestWeighing) {
    return { key: "waste.list.not_weighed", params: {}, color: "amber" };
  }
  return {
    key: "waste.list.weighed",
    params: {
      kg: formatNumber(latestWeighing.netKg, locale),
      date: formatDate(latestWeighing.weighedAt, locale),
    },
    color: "green",
  };
}

export function sendOutView(lastSendOut, locale = "it") {
  if (!lastSendOut) return null;
  return {
    key: "waste.list.sent_out",
    params: {
      kg: formatNumber(lastSendOut.weightKg, locale),
      date: formatDate(lastSendOut.date, locale),
    },
  };
}

export function rowView(row, locale = "it") {
  return {
    id: row.id,
    name: row.name,
    typeKey: `waste.type.${row.type}`,
    unitKey: `waste.unit.${row.unit}`,
    inactive: row.status === "inactive",
    recorded: recordedText(row, locale),
    weighing: weighingView(row.latestWeighing, locale),
    sendOut: sendOutView(row.lastSendOut, locale),
  };
}

// The totals line of a group: stock per unit (only the non-zero parts), bodies, weighed kg, sent
// out this month. Each part is { key, params }.
export function totalsParts(totals, locale = "it") {
  const stock = (totals && totals.stock) || {};
  const parts = [];
  if (stock.l) {
    parts.push({
      key: "waste.list.totals.litres",
      params: { value: formatNumber(stock.l, locale) },
    });
  }
  if (stock.kg) {
    parts.push({
      key: "waste.list.totals.kg",
      params: { value: formatNumber(stock.kg, locale) },
    });
  }
  if (stock.bodies) {
    parts.push({
      key: "waste.list.totals.bodies",
      params: { count: stock.bodies },
    });
  }
  if (!parts.length) parts.push({ key: "waste.list.totals.empty", params: {} });
  if (stock.weighedKg) {
    parts.push({
      key: "waste.list.totals.weighed",
      params: { value: formatNumber(stock.weighedKg, locale) },
    });
  }
  parts.push({
    key: "waste.list.totals.sent_out_month",
    params: {
      value: formatNumber((totals && totals.sentOutThisMonthKg) || 0, locale),
    },
  });
  return parts;
}

export function groupView(group, locale = "it") {
  return {
    codeKey: group.codeKey,
    code: group.code,
    description: group.description,
    hazardous: group.hazardous === true,
    totals: totalsParts(group.totals, locale),
    rows: (group.containers || []).map((row) => rowView(row, locale)),
  };
}

// The empty-state text: nothing at all, nothing for a search or code, or nothing unweighed.
export function emptyKey(view, { query, code } = {}) {
  if (query || code) return "waste.list.empty_search";
  if (view === "not_weighed") return "waste.list.empty_not_weighed";
  return "waste.list.empty";
}

// A container QR holds the plain container id (D3).
export function isContainerId(text) {
  return typeof text === "string" && /^[0-9a-f]{24}$/i.test(text.trim());
}
